import { useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { Icon } from "./Icon";
import { useI18n } from "../lib/i18n";
import { withBase } from "../lib/base";
import {
  effectivePrice,
  fetchIntroOffer,
  fetchPlans,
  formatAmount,
  preferredCurrency,
  type AccountState,
  type IntroOffer,
  type Plan,
} from "../lib/account";

/**
 * The trial countdown, and what a family sees when it runs out.
 *
 * ── What the paywall deliberately does not do ───────────────────────────────
 * It does not take the record away. Everything a family built — the
 * photographs, the mastery map, the days worked — stays readable forever; what
 * stops is the NEXT day's plan. That is decent, because a parent's evidence of
 * their own child is not a hostage, and it converts better than a wall, because
 * what they made is in front of them while they decide.
 *
 * The database enforces this, not the screen: homeschool_plan_days_insert
 * requires homeschool_has_access. Hiding a button would be a suggestion.
 */

/**
 * Bank transfer and WhatsApp, until a gateway exists.
 *
 * The same one account as everywhere on alnoortuition.com — the main site keeps
 * it in lib/payment-account.ts (owner's request, 2026-10-22: one consistent
 * way to pay on every surface, with the Raast QR). Change both together.
 */
const PAY = {
  accountTitle: "Al Noor Tuition Services",
  bank: "JazzCash",
  iban: "PK36JCMA0910923364440496",
  ibanDisplay: "PK36 JCMA 0910 9233 6444 0496",
  /** The owner's JazzCash/Raast QR card; any Pakistani banking app scans it. */
  qrCard: withBase("/al-noor-raast-qr-card.jpg"),
  whatsapp: "https://wa.me/923364440496",
  whatsappLabel: "+92 336 4440496",
};

/** An account an admin added in the main site's LMS → Payment settings. */
type ExtraAccount = {
  id: string;
  label: string;
  type: string;
  accountTitle: string | null;
  accountNumber: string | null;
  iban: string | null;
  instructions: string | null;
  qrUrl: string | null;
};

/**
 * Additional accounts, shown below the default. The main site serves them at
 * /api/payment-accounts on this same domain; if it cannot be reached (local
 * dev, an outage) the paywall simply shows the default account alone.
 */
function useExtraAccounts(): ExtraAccount[] {
  const [accounts, setAccounts] = useState<ExtraAccount[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/payment-accounts", { headers: { accept: "application/json" } })
      .then((response) => (response.ok ? (response.json() as Promise<{ additional?: ExtraAccount[] }>) : null))
      .then((body) => {
        if (!cancelled && body && Array.isArray(body.additional)) setAccounts(body.additional);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  return accounts;
}

/** Copying beats retyping 24 characters on a phone keyboard. */
function CopyIban() {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="soft-button paywall-copy"
      onClick={() => {
        void navigator.clipboard?.writeText(PAY.iban).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 2000);
        });
      }}
    >
      <Icon name={copied ? "check" : "copy"} size={14} /> {copied ? t("paywall.copied") : t("paywall.copy")}
    </button>
  );
}

function whatsappLink(message: string) {
  return `${PAY.whatsapp}?text=${encodeURIComponent(message)}`;
}

/**
 * A quiet line above the day while the free week runs.
 *
 * Deliberately not a modal and not red. A family in their trial is doing
 * exactly what we asked them to do; nagging them daily is how a good first week
 * turns into a cancelled one. It only sharpens in the last two days, when the
 * information genuinely changes what they should do.
 */
export function TrialBanner({
  account,
  onUpgrade,
}: {
  account: AccountState;
  onUpgrade: () => void;
}) {
  const { t } = useI18n();
  if (account.status !== "trialing" || !account.access) return null;
  const days = account.daysLeft ?? null;
  const urgent = days !== null && days <= 2;

  return (
    <div className={`trial-banner ${urgent ? "urgent" : ""}`}>
      <Icon name={urgent ? "clock" : "spark"} size={16} />
      <p>
        {days === null
          ? t("trial.running")
          : days <= 1
            ? t("trial.lastDay")
            : t("trial.daysLeft", { count: days })}
      </p>
      <button type="button" className="soft-button" onClick={onUpgrade}>
        {t("trial.keepGoing")}
      </button>
    </div>
  );
}

/**
 * The screen that replaces the day once the week is over.
 *
 * Everything else in the app still works. This appears where the day would be.
 */
export function Paywall({
  supabase,
  account,
  learnerName,
}: {
  supabase: SupabaseClient;
  account: AccountState;
  learnerName?: string;
}) {
  const { t } = useI18n();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [offers, setOffers] = useState<Map<string, IntroOffer>>(new Map());
  const extraAccounts = useExtraAccounts();
  const currency = preferredCurrency();

  useEffect(() => {
    let cancelled = false;
    void Promise.all([fetchPlans(supabase), fetchIntroOffer(supabase)]).then(([p, o]) => {
      if (cancelled) return;
      setPlans(p);
      setOffers(o);
    });
    return () => {
      cancelled = true;
    };
  }, [supabase]);

  // Both plans share one cohort, so any open offer means the offer is open.
  const openOffer = [...offers.values()].find((offer) => offer.is_open);

  return (
    <section className="paywall glass-card">
      <span className="icon-tile gold">
        <Icon name="star" />
      </span>
      <h2>{account.access ? t("nav.subscription") : t("paywall.title")}</h2>
      <p className="paywall-lede">{t("paywall.lede", { name: learnerName ?? t("paywall.yourChild") })}</p>

      {openOffer ? (
        <p className="paywall-offer">
          <Icon name="spark" size={14} />{" "}
          {t("paywall.introOpen", { remaining: openOffer.remaining, limit: openOffer.intro_limit ?? 0 })}
        </p>
      ) : null}

      <div className="paywall-plans">
        {plans.map((plan) => {
          const offer = offers.get(plan.code);
          const { amount, wasAmount } = effectivePrice(plan, currency, offer);
          const yearly = plan.billing_interval === "year";
          return (
            <div key={plan.code} className={`paywall-plan ${yearly ? "featured" : ""}`}>
              {yearly ? <span className="plan-flag">{t("paywall.bestValue")}</span> : null}
              <h3>{plan.title}</h3>
              <p className="plan-price">
                {wasAmount != null ? (
                  <span className="plan-was">{formatAmount(wasAmount, currency)}</span>
                ) : null}
                <strong>{formatAmount(amount, currency)}</strong>
                <small>{yearly ? t("paywall.perYear") : t("paywall.perMonth")}</small>
              </p>
              {plan.description ? <p className="plan-desc">{plan.description}</p> : null}
              <a
                className={yearly ? "primary" : "soft-button"}
                href={whatsappLink(
                  t("paywall.whatsappMessage", { plan: plan.title, price: formatAmount(amount, currency) }),
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon name="message" size={15} /> {t("paywall.choose")}
              </a>
            </div>
          );
        })}
      </div>

      {/* Manual payment, stated plainly rather than dressed up as a checkout.
          A family who is told "pay by transfer and message us" and then finds a
          card form has been misled; one told the opposite is worse. */}
      <div className="paywall-pay">
        <h3>{t("paywall.howToPay")}</h3>
        <ol>
          <li>
            {t("paywall.step1")} <strong>{PAY.accountTitle}</strong> ({PAY.bank}) — <code>{PAY.ibanDisplay}</code>{" "}
            <CopyIban />
          </li>
          <li>{t("paywall.step2")}</li>
          <li>{t("paywall.step3")}</li>
        </ol>
        <figure className="paywall-qr">
          <img src={PAY.qrCard} width={900} height={911} loading="lazy" decoding="async" alt={t("paywall.qrAlt")} />
          <figcaption>{t("paywall.qrCaption")}</figcaption>
          <a className="soft-button" href={PAY.qrCard} download="al-noor-tuition-services-raast-qr.jpg">
            <Icon name="download" size={14} /> {t("paywall.qrSave")}
          </a>
        </figure>
        {extraAccounts.length ? (
          <div className="paywall-extra">
            <p className="paywall-extra-title">{t("paywall.otherAccounts")}</p>
            <ul>
              {extraAccounts.map((account) => (
                <li key={account.id}>
                  <span>
                    <strong>{account.label}</strong> · {account.type}
                  </span>
                  {account.accountTitle ? <span className="paywall-extra-line">{account.accountTitle}</span> : null}
                  {account.iban || account.accountNumber ? (
                    <code>{account.iban ?? account.accountNumber}</code>
                  ) : null}
                  {account.qrUrl ? (
                    <a href={account.qrUrl} target="_blank" rel="noopener noreferrer">
                      {t("paywall.openQr")}
                    </a>
                  ) : null}
                  {account.instructions ? <span className="paywall-extra-line">{account.instructions}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <a
          className="primary"
          href={whatsappLink(t("paywall.whatsappGeneric"))}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Icon name="message" size={15} /> {t("paywall.whatsappUs", { number: PAY.whatsappLabel })}
        </a>
        <p className="paywall-note">{t("paywall.manualNote")}</p>
      </div>

      <p className="paywall-keep">
        <Icon name="shield" size={14} /> {t("paywall.recordKept")}
      </p>
    </section>
  );
}
